import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import fr from '../i18n/fr';

// HARDENING V1 (LOT 5) — le dictionnaire EN (≈180 Ko source) n'est plus
// embarqué dans le bundle initial : il est chargé à la demande (visiteur EN
// ou bascule de langue). FR reste synchrone : langue par défaut ET langue de
// repli de t() — aucun texte manquant pendant le chargement du chunk EN.
const SUPPORTED = ['fr', 'en'];
const STORAGE_KEY = 'wmgra_lang';
const API = process.env.REACT_APP_BACKEND_URL;

function getInitialLang() {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  }
  return 'fr';
}

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(getInitialLang);
  const [enDict, setEnDict] = useState(null);
  const [overrides, setOverrides] = useState({ fr: {}, en: {} });
  const overridesFetched = useRef(false);
  const translations = enDict ? { fr, en: enDict } : { fr };

  // LOT 5 — chunk EN chargé dès que la langue EN est active (ou détectée).
  useEffect(() => {
    if (lang === 'en' && !enDict) {
      import('../i18n/en').then((m) => setEnDict(m.default)).catch(() => {});
    }
  }, [lang, enDict]);

  // Fetch CMS i18n overrides once on mount
  useEffect(() => {
    if (overridesFetched.current) return;
    overridesFetched.current = true;
    axios.get(`${API}/api/i18n-overrides/published`)
      .then(res => { if (res.data) setOverrides(res.data); })
      .catch(() => { /* silent — use defaults */ });
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((newLang) => {
    if (SUPPORTED.includes(newLang)) {
      setLangState(newLang);
      localStorage.setItem(STORAGE_KEY, newLang);
    }
  }, []);

  const syncFromProfile = useCallback((preferredLang) => {
    if (preferredLang && SUPPORTED.includes(preferredLang)) {
      setLangState(preferredLang);
      localStorage.setItem(STORAGE_KEY, preferredLang);
    }
  }, []);

  const t = useCallback((key) => {
    // Check CMS overrides first
    const langOverrides = overrides[lang] || {};
    if (langOverrides[key]) return langOverrides[key];

    const keys = key.split('.');
    let val = translations[lang];
    for (const k of keys) {
      if (val && typeof val === 'object' && k in val) {
        val = val[k];
      } else {
        // Check FR overrides as fallback
        const frOverrides = overrides.fr || {};
        if (frOverrides[key]) return frOverrides[key];
        // Fallback to French i18n
        let fallback = translations.fr;
        for (const fk of keys) {
          if (fallback && typeof fallback === 'object' && fk in fallback) {
            fallback = fallback[fk];
          } else {
            return key;
          }
        }
        return typeof fallback === 'string' ? fallback : key;
      }
    }
    return typeof val === 'string' ? val : key;
  }, [lang, overrides, enDict]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <I18nContext.Provider value={{ lang, setLang, syncFromProfile, t, translations: translations[lang] || fr }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export const supportedLanguages = [
  { code: 'fr', label: 'Français', flag: 'FR' },
  { code: 'en', label: 'English', flag: 'EN' },
];

export const LANGUAGES = supportedLanguages;
